var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import accordionItemStyles from '@cof/fs-accordion-core/lib/accordion.lit.min.js';
import accordionItemHostStyles from '@cof/fs-accordion-core/lib/accordion-item.host.lit.min.js';
import { css, html, LitElement } from 'lit';
import { property, query } from 'lit/decorators.js';
export const FsAccordionItemToggleEventType = 'toggle';
const eventForwarder = (target) => (event) => {
    event.stopPropagation();
    const e = new Event(event.type, event);
    target.dispatchEvent(e);
};
/**
 * `FsAccordionItem` implements the Financial Services implementation of the AccordionItem
 * component.
 * Be sure to consult with the design guidelines for the AccordionItem component to ensure proper state
 * usage, it is possible to force the AccordionItem component into an unsupported state.
 *
 * ## Installation
 * ### Install the node module:
 * ```bash
 * npm i @cof/fs-accordion-wc
 * ```
 *
 * ## Usage
 * To import the side-effectful registration of `<fs-accordion-item>` call
 *
 * ```javascript
 * import '@cof/fs-accordion-wc/fs-accordion.js';
 * ```
 *
 * To import the `FsAccordionItem` class as either a base class or to self-register the custom element, use
 *
 * ```javascript
 * import { FsAccordionItem } from '@cof/fs-accordion-wc';
 * ```
 *
 * ## Anatomy
 * ### DOM Structure
 * ```html
 *   <fs-accordion-item
 *     ?disabled="false"
 *     ?expanded="true"
 *     iconPosition="trailing"
 *     ?inset="false"
 *     size="medium"
 *   >
 *     <h2 slot="fs-accordion-item-title">Accordion item title</h2>
 *     <p slot="fs-accordion-item-content">Accordion item content</p>
 *   </fs-accordion-item>
 * ```
 *
 * @element fs-accordion-item
 * @slot fs-accordion-item-title - The title for the FsAccordionItem. Must be one of h1, h2, h3, h4, h5, h6.
 * @slot fs-accordion-item-content - The content of the FsAccordionItem.
 */
export class FsAccordionItem extends LitElement {
    constructor() {
        super(...arguments);
        /**
         * Whether or not the accordion item is disabled.
         *
         * @type boolean
         */
        this.disabled = false;
        /**
         * Whether or not the accordion item is expanded.
         *
         * @type boolean
         */
        this.expanded = false;
        /**
         * Whether or not the accordion item is focused.
         *
         * @type boolean
         */
        this.focused = false;
        /**
         * The position of the collapse / expand icon in child accordion items.
         *
         * Must be one of 'leading' or 'trailing'.
         *
         * @type FsAccordionIconPosition
         */
        this.iconPosition = null;
        /**
         * The index of the accordion item in the parent accordion.
         *
         * @type number
         */
        this.index = null;
        /**
         * Whether or not the child accordion items have extra "inset" padding.
         *
         * @type boolean
         */
        this.inset = null;
        /**
         * The size of the child accordion items.
         *
         * Must be one of 'small', 'medium', or 'large'.
         *
         * @type FsAccordionSize
         */
        this.size = null;
        this.forwardEvent = eventForwarder(this);
    }
    static get styles() {
        return [
            css `
        :host {
          display: block;
        }
      `,
            accordionItemStyles,
            accordionItemHostStyles
        ];
    }
    _handleBlur(event) {
        this.forwardEvent(event);
        this.focused = false;
        return false;
    }
    _handleFocus(event) {
        this.forwardEvent(event);
        this.focused = true;
        return false;
    }
    _handleToggle(event) {
        const eventToDispatch = new CustomEvent(FsAccordionItemToggleEventType, {
            cancelable: true,
            detail: {
                expanded: !this.expanded
            }
        });
        this.dispatchEvent(eventToDispatch);
        if (eventToDispatch.defaultPrevented) {
            return;
        }
        event.preventDefault();
        this.expanded = !this.expanded;
        return false;
    }
    /**
     * Updates the body height variable for use in expand / collapse animations. This is run post render.
     * @private
     */
    _handleUpdateBodyHeight() {
        const paddingTopCssString = window
            ?.getComputedStyle(this.accordionItemBody)
            .getPropertyValue('--intended-padding-top');
        const paddingBottomCssString = window
            ?.getComputedStyle(this.accordionItemBody)
            .getPropertyValue('--intended-padding-bottom');
        const paddingTop = Number(paddingTopCssString.replace(/[a-z]/gi, ''));
        const paddingBottom = Number(paddingBottomCssString.replace(/[a-z]/gi, ''));
        const height = this.accordionItemBody.scrollHeight;
        const totalAccordionItemHeight = paddingTop + paddingBottom + height;
        const totalAccordionItemHeightCssString = `${totalAccordionItemHeight}px`;
        this.accordionItemBody.style.setProperty('--accordion-body-height', totalAccordionItemHeightCssString);
    }
    _handleUpdateFocus() {
        if (this.focused) {
            this.accordionItemHeading.focus();
        }
        else {
            this.accordionItemHeading.blur();
        }
    }
    /**
     * Render the component.
     *
     * @returns TemplateResult
     */
    render() {
        return html `
    <div class="
      fs-accordion-item
      fs-accordion-item--${this.expanded ? 'expanded' : 'collapsed'}
      fs-accordion-item--${this.size}
      fs-accordion-item--${this.iconPosition}
      ${this.inset ? `fs-accordion-item--inset` : ''}
      ${this.disabled ? 'fs-accordion-item--disabled' : ''}
      "
    >
      <button
        aria-controls="fs-accordion-item-body"
        aria-expanded="${this.expanded ? 'true' : 'false'}"
        class="fs-accordion-item__heading fs-accordion-item__heading"
        id="fs-accordion-item-heading"
        ?disabled=${this.disabled}
        @blur=${this._handleBlur}
        @click=${this._handleToggle}
        @focus=${this._handleFocus}
      >
        <slot name="fs-accordion-item-title"></slot>
        <span class="fs-accordion-item__heading-icon"></span>
      </button>
      <div
        aria-labelledby="fs-accordion-item-heading"
        class="fs-accordion-item__body"
        id="fs-accordion-item-body"
        role="region"
      >
        <slot name="fs-accordion-item-content"></slot>
      </div>
    </div>
    `;
    }
    /**
     * Lifecycle hook called with the component updates for the first time.
     *
     * @protected
     */
    firstUpdated() {
        /** Use this to target in the accordion parent to hide any non-allowed children */
        this.toggleAttribute('fs-accordion-item', true);
    }
    /**
     * Lifecycle hook called when the component has been updated.
     *
     * @protected
     */
    updated(changedProperties) {
        this._handleUpdateBodyHeight();
        if (changedProperties.get('focused') != null) {
            this._handleUpdateFocus();
        }
    }
}
/** The element's canonical name for use with safe define */
FsAccordionItem.canonicalName = 'accordion-item';
__decorate([
    property({ type: Boolean, reflect: true })
], FsAccordionItem.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsAccordionItem.prototype, "expanded", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsAccordionItem.prototype, "focused", void 0);
__decorate([
    property({ attribute: 'icon-position', type: String })
], FsAccordionItem.prototype, "iconPosition", void 0);
__decorate([
    property({ type: Number, reflect: true })
], FsAccordionItem.prototype, "index", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], FsAccordionItem.prototype, "inset", void 0);
__decorate([
    property({ type: String, reflect: true })
], FsAccordionItem.prototype, "size", void 0);
__decorate([
    query('#fs-accordion-item-body')
], FsAccordionItem.prototype, "accordionItemBody", void 0);
__decorate([
    query('#fs-accordion-item-heading')
], FsAccordionItem.prototype, "accordionItemHeading", void 0);
//# sourceMappingURL=AccordionItem.js.map